import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "api/landlord/applications";
import {
  IncomeSourceProvider,
  VerificationNames,
  VerificationStatus,
  VerifyMethodType
} from "shared/types";
import { VerificationReport } from "types/landlord/reports";
import { getVerificationQueryKeys } from "views/applications-id-report/utils/getVerificationQueryKeys";
import { CombinedIncomeReport } from "../../../types";
import { getReverifyRequests } from "../../../slices/ReverifySlice/utils";
import { getFilteredBankStatements, filterIncomes } from "../utils";
import templatesApi from "api/landlord/templates";

interface Props {
  applicationId?: number;
  applicantId?: number;
  currentlySelectedVerificationId?: number;
}

export const useReportDataController = ({
  applicationId,
  applicantId,
  currentlySelectedVerificationId
}: Props) => {
  const {
    data: verificationData,
    isError,
    isLoading,
    refetch
  } = useQuery<VerificationReport<CombinedIncomeReport>>({
    queryKey: getVerificationQueryKeys({
      reportName: VerificationNames.CombinedIncome,
      applicationId: Number(applicationId || 0),
      applicantId: Number(applicantId || 0),
      verificationId: currentlySelectedVerificationId
    }),
    queryFn: () =>
      api
        .getApplicantVerification(
          Number(applicationId || 0),
          Number(applicantId || 0),
          currentlySelectedVerificationId || 0
        )
        .then((data) => data.verification)
  });

  const composedReportData = useMemo(() => {
    const verificationReportData = verificationData?.report;
    const settings = verificationData?.settings;
    const verificationStatus =
      verificationData?.status ?? VerificationStatus.Pending;
    const {
      pendingReverifyRequests,
      completedReverifyRequests,
      failedReverifyRequests
    } = getReverifyRequests(verificationData);

    const { incomeSourceTrustScoresMap, incomeSourceRejectionReasonsMap } = (
      verificationData?.report?.incomes ?? []
    )
      .flatMap((income) => income.sources ?? [])
      .reduce<{
        incomeSourceTrustScoresMap: Record<number, number>;
        incomeSourceRejectionReasonsMap: Record<number, string[]>;
      }>(
        (acc, s) => {
          if (s.trust_score !== undefined)
            acc.incomeSourceTrustScoresMap[s.id] = s.trust_score;
          if (s.rejection_reason?.length)
            acc.incomeSourceRejectionReasonsMap[s.id] = s.rejection_reason;

          return acc;
        },
        { incomeSourceTrustScoresMap: {}, incomeSourceRejectionReasonsMap: {} }
      );

    const incomeSourceProvidersMap = Object.fromEntries(
      (verificationData?.income_sources ?? []).map((s) => [
        s.id,
        s.provider ?? ""
      ])
    ) as Record<number, string>;

    const plaidBankIncomeSources = (
      verificationData?.income_sources ?? []
    ).filter(
      (s) =>
        s.type === VerifyMethodType.Bank &&
        s.provider === IncomeSourceProvider.Plaid
    );

    return {
      verificationReportData,
      settings,
      verificationStatus,
      filteredBankStatements: getFilteredBankStatements(verificationData),
      pendingReverifyRequests,
      completedReverifyRequests,
      failedReverifyRequests,
      incomes: verificationReportData
        ? filterIncomes(verificationReportData.incomes, verificationStatus)
        : [],
      incomeSourceProvidersMap,
      incomeSourceRejectionReasonsMap,
      incomeSourceTrustScoresMap,
      plaidBankIncomeSources
    };
  }, [verificationData]);

  const defaultVerificationSchemasQuery = useQuery({
    queryKey: ["VERIFICATION_SCHEMAS"],
    queryFn: () => templatesApi.getVerificationSchemas()
  });

  const defaultCombinedIncomeVerificationSchema =
    defaultVerificationSchemasQuery.data?.verification_schemas.find(
      (v) => v.name === VerificationNames.CombinedIncome
    );

  return {
    verificationData,
    defaultCombinedIncomeVerificationSchema,
    ...composedReportData,
    isLoading,
    isError,
    refetch
  };
};
